import { useCallback, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'adel.theme';

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Twelve lines instead of `next-themes`.
 *
 * The initial class is set by an inline script in index.html so there is no
 * flash of the wrong ground colour; this hook only has to stay in step with it.
 * Dark-first: without a stored choice the console follows the machine, and a
 * machine with no preference gets dark.
 */
export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void; toggle: () => void } {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof document === 'undefined') return 'dark';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    document.documentElement.classList.toggle('dark', next === 'dark');
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode: the choice lasts for this tab, which is enough */
    }
  }, []);

  // Follow the system only while the operator has not chosen for themselves.
  useEffect(() => {
    if (readStored() !== null) return;
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const sync = (): void => {
      const next: Theme = media.matches ? 'light' : 'dark';
      setThemeState(next);
      document.documentElement.classList.toggle('dark', next === 'dark');
    };
    media.addEventListener('change', sync);
    return () => {
      media.removeEventListener('change', sync);
    };
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, setTheme, toggle };
}
